import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Trash2, CalendarOff } from 'lucide-react';
import dayjs from 'dayjs';
import api from '../../lib/axios';
import PageHeader    from '../../components/ui/PageHeader';
import EmptyState    from '../../components/ui/EmptyState';
import Modal         from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

const schema = z.object({
  staff_id: z.string().uuid().or(z.literal('')).optional(),
  start_at: z.string().min(1, 'Required'),
  end_at:   z.string().min(1, 'Required'),
  reason:   z.string().max(255).optional(),
}).refine(d => !d.start_at || !d.end_at || d.end_at > d.start_at, {
  message: 'End must be after start', path: ['end_at'],
});

function AddModal({ open, onClose }) {
  const qc = useQueryClient();

  const { data: staff } = useQuery({
    queryKey: ['staff'],
    queryFn:  () => api.get('/staff').then(r => r.data.data),
  });

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { staff_id: '', reason: '' },
  });

  const mut = useMutation({
    mutationFn: (data) => api.post('/time-off', {
      ...data,
      staff_id: data.staff_id || null,
      // Convert local datetime-local value to ISO string
      start_at: new Date(data.start_at).toISOString(),
      end_at:   new Date(data.end_at).toISOString(),
    }),
    onSuccess: () => { qc.invalidateQueries(['time-off']); reset(); onClose(); },
  });

  return (
    <Modal open={open} onClose={onClose} title="Add time off / holiday" size="md">
      <form onSubmit={handleSubmit(d => mut.mutateAsync(d))} className="space-y-4">
        <div>
          <label className="label">Staff member (leave blank for whole business)</label>
          <select {...register('staff_id')} className="input">
            <option value="">— All staff / Business closure —</option>
            {(staff || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Start</label>
            <input {...register('start_at')} type="datetime-local" className={`input ${errors.start_at ? 'border-red-400' : ''}`} />
            {errors.start_at && <p className="mt-1 text-xs text-red-500">{errors.start_at.message}</p>}
          </div>
          <div>
            <label className="label">End</label>
            <input {...register('end_at')} type="datetime-local" className={`input ${errors.end_at ? 'border-red-400' : ''}`} />
            {errors.end_at && <p className="mt-1 text-xs text-red-500">{errors.end_at.message}</p>}
          </div>
        </div>

        <div>
          <label className="label">Reason (optional)</label>
          <input {...register('reason')} className="input" placeholder="e.g. Public holiday, vacation…" />
        </div>

        {mut.isError && <p className="text-xs text-red-500">{mut.error?.response?.data?.error?.message || 'Save failed'}</p>}

        <div className="flex justify-end gap-3 pt-2 border-t border-surface-border">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={isSubmitting} className="btn-primary">Add entry</button>
        </div>
      </form>
    </Modal>
  );
}

export default function TimeOff() {
  const qc = useQueryClient();
  const [addOpen,      setAddOpen]      = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['time-off'],
    queryFn:  () => api.get('/time-off').then(r => r.data.data),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/time-off/${id}`),
    onSuccess:  () => { qc.invalidateQueries(['time-off']); setDeleteTarget(null); },
  });

  const entries = data || [];
  const upcoming = entries.filter(e => new Date(e.end_at) >= new Date());
  const past     = entries.filter(e => new Date(e.end_at) <  new Date());

  function fmt(dt) {
    return dayjs(dt).format('MMM D, YYYY h:mm A');
  }

  function Row({ entry }) {
    return (
      <div className="flex items-center gap-4 py-3 border-b border-surface-border last:border-0">
        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: entry.Staff?.color || '#94a3b8' }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-ink">
            {entry.Staff ? entry.Staff.name : 'All staff'}
            {entry.reason && <span className="text-ink-secondary font-normal"> · {entry.reason}</span>}
          </p>
          <p className="text-xs text-ink-muted">{fmt(entry.start_at)} — {fmt(entry.end_at)}</p>
        </div>
        <button onClick={() => setDeleteTarget(entry)} className="btn-ghost p-1.5 text-red-500 hover:bg-red-50 shrink-0">
          <Trash2 size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      <PageHeader
        title="Time Off & Holidays"
        subtitle="Block time when staff or the whole business is unavailable"
        action={
          <button onClick={() => setAddOpen(true)} className="btn-primary">
            <Plus size={16} /> Add entry
          </button>
        }
      />

      {isLoading ? (
        <div className="card p-6 space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-10 animate-pulse bg-surface-100 rounded" />)}
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={CalendarOff}
          title="No time-off entries"
          description="Add closures, holidays, or personal time off."
          action={<button onClick={() => setAddOpen(true)} className="btn-primary">Add first entry</button>}
        />
      ) : (
        <div className="space-y-4">
          {upcoming.length > 0 && (
            <div className="card p-5">
              <h2 className="text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-2">Upcoming</h2>
              {upcoming.map(e => <Row key={e.id} entry={e} />)}
            </div>
          )}
          {past.length > 0 && (
            <div className="card p-5 opacity-60">
              <h2 className="text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-2">Past</h2>
              {past.map(e => <Row key={e.id} entry={e} />)}
            </div>
          )}
        </div>
      )}

      <AddModal open={addOpen} onClose={() => setAddOpen(false)} />
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMut.mutate(deleteTarget?.id)}
        loading={deleteMut.isPending}
        title="Delete time-off entry?"
        message="This block will be removed and the time may become bookable again."
      />
    </div>
  );
}
